import React, { useEffect, useRef, useState, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { ShieldCheck, Fingerprint, LogOut } from "lucide-react";
import { toast } from "sonner";
import { isInstalledPWA, isMobileDevice, webauthnSupported, getStatus, registerDevice, unlockDevice, lockDevice, isCancelled, isTimeout } from "../lib/webauthn";

// Biometric app-lock for the INSTALLED PWA only. Browser-tab / desktop web users are not locked.
const LockOverlay = ({ children }) => {
  const { user, logout } = useAuth();
  const [locked, setLocked] = useState(true);
  const [enrolled, setEnrolled] = useState(null); // null = unknown
  const [busy, setBusy] = useState(false);
  const [needsSetup, setNeedsSetup] = useState(false); // this device has no matching passkey yet
  const [statusError, setStatusError] = useState(false);
  const uidRef = useRef(null);
  const pickerRef = useRef(0);

  const installed = isInstalledPWA();
  const active = installed && isMobileDevice() && !!user && !window.location.pathname.startsWith("/share/");

  // Re-lock when the signed-in user changes (fresh login).
  useEffect(() => {
    if (user?.user_id && user.user_id !== uidRef.current) {
      uidRef.current = user.user_id;
      setLocked(true);
      setEnrolled(null);
      setNeedsSetup(false);
      setStatusError(false);
    }
    if (!user) uidRef.current = null;
  }, [user]);

  // Re-lock when the app is backgrounded and returns — but NOT when returning from the native
  // file/camera picker (which backgrounds the PWA on iOS), and not for very brief switches.
  // This stops users being locked out every time they attach a photo, while still locking on a
  // genuine minimise / after a real absence.
  const timeoutMin = user?.lock_timeout_minutes || 0;
  useEffect(() => {
    const GRACE_MS = timeoutMin > 0 ? timeoutMin * 60 * 1000 : 20 * 1000;
    const PICKER_WINDOW_MS = Math.max(10 * 60 * 1000, GRACE_MS);
    let hiddenAt = 0;

    const onPickerClick = (e) => {
      const t = e.target;
      if (t && t.tagName === "INPUT" && t.type === "file") pickerRef.current = Date.now();
    };
    const markHidden = () => { hiddenAt = Date.now(); };
    const maybeRelock = () => {
      if (document.visibilityState !== "visible") return;
      const now = Date.now();
      const fromPicker = pickerRef.current && now - pickerRef.current < PICKER_WINDOW_MS;
      const quick = hiddenAt && now - hiddenAt < GRACE_MS;
      pickerRef.current = 0;
      hiddenAt = 0;
      if (fromPicker || quick) return;
      setLocked(true);
      if (active) lockDevice();
    };
    const onVis = () => {
      if (document.visibilityState === "hidden") markHidden();
      else maybeRelock();
    };

    document.addEventListener("click", onPickerClick, true);
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", maybeRelock);
    window.addEventListener("pagehide", markHidden);
    return () => {
      document.removeEventListener("click", onPickerClick, true);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", maybeRelock);
      window.removeEventListener("pagehide", markHidden);
    };
  }, [timeoutMin]);

  // Determine enrolment status when we become active and locked.
  useEffect(() => {
    if (active && locked && enrolled === null && !statusError && webauthnSupported()) {
      getStatus()
        .then((s) => { setEnrolled(!!s.enrolled); if (!s.locked) setLocked(false); setStatusError(false); })
        .catch(() => setStatusError(true));
    }
  }, [active, locked, enrolled, statusError]);

  const doUnlock = useCallback(async () => {
    setBusy(true);
    try {
      await unlockDevice();
      setNeedsSetup(false);
      setLocked(false);
    } catch (e) {
      if (e?.response?.status === 404) setEnrolled(false);
      else if (e?.code === "NO_AUTHENTICATOR") toast.error("Add a fingerprint, Face ID or screen lock in your phone settings, then reopen the installed TilesMate app.");
      else if (isTimeout(e)) toast.error("Unlock timed out. Tap Unlock to try again.");
      else if (isCancelled(e)) setNeedsSetup(true); // cancelled, or no matching passkey on this device
      else toast.error("Unlock failed. Please try again.");
    } finally {
      setBusy(false);
    }
  }, []);

  const doRegister = useCallback(async () => {
    setBusy(true);
    try {
      await registerDevice();
      setEnrolled(true);
      setNeedsSetup(false);
      setLocked(false);
      toast.success("Device unlock set up");
    } catch (e) {
      if (e?.code === "NO_AUTHENTICATOR") toast.error("Add a fingerprint, Face ID or screen lock in your phone settings, then reopen the installed TilesMate app.");
      else if (e?.name === "InvalidStateError") toast.error("This device is already set up — tap Unlock instead.");
      else if (isTimeout(e)) toast.error("Setup timed out. Please try again.");
      else if (isCancelled(e)) toast.error("Setup didn't finish. Open the installed TilesMate app (not a browser tab) and try again.");
      else toast.error("Couldn't set up device unlock on this device.");
    } finally {
      setBusy(false);
    }
  }, []);

  if (!active || !locked) return <>{children}</>;

  const unsupported = !webauthnSupported();

  return (
    <div data-testid="lock-overlay" className="fixed inset-0 z-[100] bg-[#1A1A1A] text-white flex flex-col items-center justify-center px-6 pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]">
      <div className="w-full max-w-sm text-center">
        <div className="mx-auto w-16 h-16 rounded-2xl bg-white/10 flex items-center justify-center mb-6">
          <ShieldCheck size={32} className="text-[#E08A2B]" />
        </div>
        <h1 className="text-2xl font-bold mb-2">App Locked</h1>

        {unsupported ? (
          <p className="text-white/70 text-sm mb-8">This device doesn't support biometric unlock. Contact your admin.</p>
        ) : statusError ? (
          <p className="text-white/70 text-sm mb-8">Couldn't reach the server. Check your connection and try again.</p>
        ) : enrolled === null ? (
          <p className="text-white/60 text-sm mb-8">Checking secure unlock…</p>
        ) : enrolled === false ? (
          <p className="text-white/70 text-sm mb-8">Set up fingerprint / Face ID so only you can open TilesMate on this device.</p>
        ) : (
          <p className="text-white/70 text-sm mb-8">Unlock with your fingerprint, Face ID or device lock to continue.</p>
        )}

        {!unsupported && statusError && (
          <button data-testid="lock-retry-btn" onClick={() => { setStatusError(false); setEnrolled(null); }} className="w-full bg-white text-[#1A1A1A] rounded-lg py-3 font-semibold transition-opacity">
            Try again
          </button>
        )}

        {!unsupported && !statusError && enrolled === false && (
          <button data-testid="lock-setup-btn" disabled={busy} onClick={doRegister} className="w-full bg-white text-[#1A1A1A] rounded-lg py-3 font-semibold flex items-center justify-center gap-2 disabled:opacity-60 transition-opacity">
            <Fingerprint size={20} /> {busy ? "Setting up…" : "Set up device unlock"}
          </button>
        )}

        {!unsupported && enrolled === true && (
          <>
            <button data-testid="lock-unlock-btn" disabled={busy} onClick={doUnlock} className="w-full bg-[#E08A2B] text-[#1A1A1A] rounded-lg py-3 font-semibold flex items-center justify-center gap-2 disabled:opacity-60 transition-opacity">
              <Fingerprint size={20} /> {busy ? "Unlocking…" : "Unlock"}
            </button>
            {needsSetup && (
              <button data-testid="lock-setup-device-btn" disabled={busy} onClick={doRegister} className="mt-3 w-full border border-white/30 rounded-lg py-3 font-medium text-sm hover:bg-white/5 transition-colors">
                Set up unlock on this device
              </button>
            )}
          </>
        )}

        <button data-testid="lock-signout-btn" onClick={logout} className="mt-8 text-white/60 text-sm inline-flex items-center gap-2 hover:text-white transition-colors">
          <LogOut size={16} /> Sign out
        </button>
      </div>
    </div>
  );
};

export default LockOverlay;
